
import { useStore } from "vuex";
import { computed, onMounted, ref, watch } from "vue";
import { VOTE_ACTIONS, VOTE_GETTERS, VOTE_MUTATIONS } from "@/store/vote";
import elrondHelper from "@/helpers/elrond";
import { USER_GETTERS } from "@/store/user";
import { BASE_MUTATIONS } from "@/store";
import memeVotingContract, { MEME_VOTING_CONSTANTS, MEME_VOTING_FUNCTIONS } from "@/contracts/memeVoting";
import { MEME_MUTATIONS } from "@/store/meme";
import Loader from "@/components/helpers/Loader.vue";
import { Account } from "@multiversx/sdk-core";
import BLOCKCHAIN from "@/constants/blockchain";
import ENV from "@/constants/env";
import memeClient from "@/api/meme";
import TokenImage from "@/components/TokenImage.vue";
import { TokenTransfer, Transaction } from "@multiversx/sdk-core/out";
import oneDexContract from "@/contracts/oneDex";
import wegldSwapContract from "@/contracts/wegldSwap";
import { BigNumber } from "bignumber.js";

export default {
  name: "VotesSubmit",
  components: { TokenImage, Loader },
  setup() {
    const store = useStore();
    const tempVotes = computed(() => store.getters[VOTE_GETTERS.TEMP]);
    const tempNuts = computed(() => store.getters[VOTE_GETTERS.TEMP_NUTS]);

    const loading = ref(false);
    const NUTS_TOKEN = BLOCKCHAIN.TOKEN_IDENTIFIER;

    const nutsBalance = computed<TokenTransfer>(() => store.getters[USER_GETTERS.TOKENS][BLOCKCHAIN.TOKEN_IDENTIFIER]);
    const wegldBalance = computed<TokenTransfer>(
      () => store.getters[USER_GETTERS.TOKENS][BLOCKCHAIN.TOKEN_IDENTIFIER_WEGLD]
    );

    const accountElrond = computed<Account | null>(() => store.getters[USER_GETTERS.ACCOUNT_ELROND]);

    const checkTransactionStatus = async (transaction?: Transaction) => {
      const pendingTransactionStatus = await elrondHelper.getPendingTransaction(
        BLOCKCHAIN.MEMES_VOTING_CONTRACT,
        MEME_VOTING_FUNCTIONS.VOTE_MEMES,
        transaction
      );

      if (null !== pendingTransactionStatus) {
        // @ts-ignore
        if (true === pendingTransactionStatus) {
          store.commit(VOTE_MUTATIONS.USER_REMAINING_REMOVE);
          store.commit(MEME_MUTATIONS.ADD_VOTES, tempVotes.value);
          voteDiscard();

          store.commit(BASE_MUTATIONS.MESSAGE_MODAL, {
            variant: "success",
            message: "meme.vote.success",
          });
        }
        // @ts-ignore
        else if (pendingTransactionStatus === MEME_VOTING_CONSTANTS.ERRORS.VOTES) {
          voteDiscard();

          store.commit(BASE_MUTATIONS.MESSAGE_MODAL, {
            variant: "danger",
            message: "meme.vote.error",
          });
        } else {
          store.commit(BASE_MUTATIONS.MESSAGE_MODAL, {
            variant: "danger",
            message: "messages.error.transaction",
          });
        }
      }
    };

    const voteSubmitCaptcha = async (captchaResult) => {
      loading.value = true;

      const transactions = [];

      if (tempNuts.value > nutsBalance.value.amount) {
        const requiredWegldForSwap = (
          await oneDexContract.getAmountIn(
            BLOCKCHAIN.TOKEN_IDENTIFIER_WEGLD,
            BLOCKCHAIN.TOKEN_IDENTIFIER,
            new BigNumber(tempNuts.value - nutsBalance.value.amount),
          )
        )
          .multipliedBy(1.01) // 1% slippage;
          .decimalPlaces(0);

        if (requiredWegldForSwap.gt(wegldBalance.value.amount + BigInt(accountElrond.value.balance.toString()))) {
          store.commit(BASE_MUTATIONS.MESSAGE_MODAL, {
            variant: "warning",
            message: "meme.vote.errorNuts",
          });

          loading.value = false;

          return;
        }

        // Check if we need to wrap EGLD to WEGLD
        if (requiredWegldForSwap.gt(wegldBalance.value.amount)) {
          const toWrap = requiredWegldForSwap.minus(wegldBalance.value.amount);

          const wrapTx = wegldSwapContract.wrapEgld(accountElrond.value.address.bech32(), toWrap.toString());
          transactions.push(wrapTx);
        }

        const swapTx = await oneDexContract.swapMultiTokensFixedOutput(
          BLOCKCHAIN.TOKEN_IDENTIFIER_WEGLD,
          BLOCKCHAIN.TOKEN_IDENTIFIER,
          requiredWegldForSwap,
          new BigNumber(tempNuts.value - nutsBalance.value.amount),
        );
        transactions.push(swapTx);
      }

      const response = await memeClient.memeVote(tempVotes.value, captchaResult);

      if (!response) {
        loading.value = false;

        store.commit(BASE_MUTATIONS.MESSAGE_MODAL, {
          variant: "danger",
          message: "meme.vote.errorCaptcha",
        });

        return;
      }

      let transaction = await memeVotingContract.voteMemes(response.signature, tempVotes.value, tempNuts.value);
      transactions.push(transaction);

      try {
        const result = await elrondHelper.sendTransactions(transactions, accountElrond.value);

        if (!result || !result.length) {
          loading.value = false;
          return;
        }

        const promises = [];

        if (result.length > 0) {
          result.slice(0, -1).forEach((tx) => {
            promises.push(elrondHelper.getPendingTransaction(null, null, tx));
          });
        }

        transaction = result[result.length - 1];
        promises.push(checkTransactionStatus(transaction));

        await Promise.all(promises);
      } catch (error) {
        console.error("Error submitting votes:", error);
        store.commit(BASE_MUTATIONS.MESSAGE_MODAL, {
          variant: "danger",
          message: "messages.error.transaction",
        });

        // @ts-ignore
        window.turnstile.reset(captchaWidgetId.value);
      } finally {
        loading.value = false;
      }
    };

    const voteSubmit = async () => {
      if (!captchaResult.value) {
        return;
      }

      await voteSubmitCaptcha(captchaResult.value);

      captchaResult.value = null;
    };

    const voteDiscard = () => {
      store.commit(VOTE_MUTATIONS.TEMP, []);
      store.commit(VOTE_MUTATIONS.TEMP_NUTS, 0n);
    };

    const captchaWidgetId = ref(null);
    const captchaResult = ref(null);
    const voteCaptchaRef = ref(null);

    watch(tempVotes, (newValue, oldValue) => {
      if (0 === oldValue.length && newValue.length > 0 && captchaWidgetId.value) {
        // @ts-ignore
        window.turnstile.reset(captchaWidgetId.value);
      }
    });

    watch(voteCaptchaRef, (newValue, oldValue) => {
      if (newValue && !oldValue) {
        // @ts-ignore
        captchaWidgetId.value = window.turnstile.render("#voteCaptcha", {
          sitekey: ENV.CAPTCHA_ID,
          callback: function (token) {
            captchaResult.value = token;
          },
        });
      }
    });

    return {
      tempVotes,
      tempNuts,
      loading,
      voteSubmit,
      voteDiscard,
      NUTS_TOKEN,
      voteCaptchaRef,
    };
  },
};
